/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2026 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2026 - Finnish Meteorological Institute (FMI)
 * Copyright 2026 - The Norwegian Meteorological Institute (MET Norway)
 * */

import type { syncGroupsTypes } from '@opengeoweb/store';
import type {
  AirmetWarning,
  SigmetWarning,
  Warning,
} from '../../store/warningsForm/types';

export interface SyncedWarningFeature {
  id: string;
  originalId: string;
  geoJSON: GeoJSON.FeatureCollection;
}

export const getSelectedFormAreaIds = (
  selectedFormWarning?: Warning,
): Set<string> =>
  new Set(
    (selectedFormWarning?.warningDetail?.areas ?? [])
      .map((area) => area.areaId)
      .filter((areaId): areaId is string => Boolean(areaId)),
  );

const isAviationWarning = (
  warning?: Warning,
): warning is AirmetWarning | SigmetWarning =>
  warning?.type === 'airmet' || warning?.type === 'sigmet';

export const isUnsavedAviationCopy = (warning?: Warning): boolean =>
  isAviationWarning(warning) &&
  Boolean(warning.id) &&
  warning.sigmetAirmetDetails.uuid === undefined;

export const shouldExcludeWarningFromFeatureSync = ({
  hiddenWarningIds,
  hasMatchingAreaId,
  selectedWarningId,
  warningIds,
  selectedFormWarning,
}: {
  hiddenWarningIds: Set<string>;
  hasMatchingAreaId: boolean;
  selectedWarningId?: string;
  warningIds: string[];
  selectedFormWarning?: Warning;
}): boolean =>
  (!isUnsavedAviationCopy(selectedFormWarning) &&
    (warningIds.includes(selectedWarningId ?? '') || hasMatchingAreaId)) ||
  warningIds.some((warningId) => hiddenWarningIds.has(warningId));

export const createWarningFeatureCollection = (
  allFeatures: SyncedWarningFeature[],
): GeoJSON.FeatureCollection<
  GeoJSON.Geometry,
  syncGroupsTypes.PotentialDataProperties | null
> =>
  ({
    type: 'FeatureCollection',
    features: allFeatures.flatMap((feature) =>
      feature.geoJSON.features.map((geoFeature) => ({
        ...geoFeature,
        properties: {
          ...(geoFeature.properties ?? {}),
          originalId: feature.originalId,
        },
      })),
    ),
  }) as GeoJSON.FeatureCollection<
    GeoJSON.Geometry,
    syncGroupsTypes.PotentialDataProperties | null
  >;
